import { DateTimeUtility, EnumToKeyValueArray, GetTeamId, GetUserId, isMobileView, removeNullAndUndefinedProperties, timeRangeValidator, uniqueFieldValidator } from '@core/utils';
import { RecurrenceDatesFormComponent } from '@shared/components/forms/recurrence-dates-form/recurrence-dates-form.component';
import { untilDestroyed } from '@ngneat/until-destroy';
import {
  ConfirmationDialogType,
  CourseInstructorType,
  DeleteConfirmationOptions,
  EventType,
  InstructorType,
  NotificationType,
  RecurrenceOption,
  RecurrenceType,
  SocketEvents,
  TagType,
  TaskType,
  Visibility,
} from '@core/enums';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CourseEntity, CourseTask, CourseTaskUser, GroupEntity, GroupParticipant, InstructorEntity, RecurringDate, RoomEntity, TaskCollaborator, UserEntity } from '@core/entities';
import { RRule, rrulestr } from 'rrule';
import { instanceToPlain } from 'class-transformer';
import { DropDownItem } from '@core/types/Dropdown.type';
import { FormService, Logger } from '@core/services';
import { UseCourse, UseGroups, UseInstructor, UseRoom, UseTag, UseTask } from '@core/usecases';
import { ChangeDetectorRef, ElementRef, inject } from '@angular/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { PERMISSIONS, PermissionService } from '@auth';
import { TranslateService } from '@ngx-translate/core';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { Router } from '@angular/router';
import { SocketIoService } from '@core/socket-io';
import { ButtonComponent } from '@shared/components/ui';
import { saveAs } from 'file-saver';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';

const log = new Logger('Course Form Composition');

export class CourseFormComposition extends FormService {
  courseId: number;

  tags: DropDownItem[] = [];
  groups: GroupEntity[] = [];
  rooms: RoomEntity[] = [];
  users: UserEntity[] = [];
  internalInstructors: InstructorEntity[] = [];
  externalInstructors: InstructorEntity[] = [];
  selectedInstructorDetails: InstructorEntity;
  selectedRoomDetails: RoomEntity;
  repeatCourse: any[] = [];
  instructorTypes: any[] = [];
  eventTypes: any[] = [];
  visibilityOfCourse: any[] = [];
  days: DropDownItem[] = [];
  showEndDate = false;
  taskTypes: any[] = [];
  selectedImage: File;
  selectedAttachment: File;

  loadings = {
    downloadingDocument: false,
    deletingDocument: false,
  };

  protected _currentSelectedCourse: CourseEntity;
  protected readonly _translateService = inject(TranslateService);
  protected readonly _dialogRef: DialogRef;
  protected readonly _bottomSheetService: NgxBottomSheetService;

  private _groupParticipants: GroupParticipant[] = [];
  private _taskGroupParticipants: GroupParticipant[] = [];

  private readonly _useCourse = new UseCourse();
  private readonly _useTags = new UseTag();
  private readonly _useGroup = new UseGroups();
  private readonly _useInstructor = new UseInstructor();
  private readonly _useRoom = new UseRoom();
  private readonly _useTask = new UseTask();

  private readonly _formBuilder = inject(FormBuilder);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _toast = inject(HotToastService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _router = inject(Router);
  private readonly _elRef = inject(ElementRef);
  private readonly _dialogService: DialogService;
  private readonly _socketService = inject(SocketIoService);

  constructor() {
    super();

    if (!isMobileView()) {
      this._dialogService = inject(DialogService);
      this._dialogRef = inject(DialogRef);
    } else {
      this._bottomSheetService = inject(NgxBottomSheetService);
    }

    this.days = DateTimeUtility.getWeekDays(this._translateService) as DropDownItem[];
    this.repeatCourse = EnumToKeyValueArray(RecurrenceType, this._translateService, true);
    this.instructorTypes = EnumToKeyValueArray(CourseInstructorType, this._translateService);
    this.visibilityOfCourse = EnumToKeyValueArray(Visibility, this._translateService);
    this.taskTypes = EnumToKeyValueArray(TaskType, this._translateService);
    this.eventTypes = EnumToKeyValueArray(EventType, this._translateService);
  }

  get canCreateCourse(): boolean {
    return this._permissionService.hasPermission(PERMISSIONS.CREATE_COURSE);
  }

  get courseDateFormArray() {
    return this.form.get('courseDate') as FormArray;
  }

  get allDaysSelected() {
    return this.courseDateFormArray.length === this.days.length;
  }

  get isSingleValidTimeTo(): boolean {
    const courseDateArray = this.courseDateFormArray;

    // Check if there is exactly one item in the form array
    if (courseDateArray.length === 1) {
      const timeToControl = courseDateArray.at(0).get('endTime');

      // Check if the endTime control exists, has a value, and is valid
      return !!timeToControl && timeToControl.value && timeToControl.valid;
    }

    return false;
  }

  get instructorAvailability(): { id: number; name: string; time: string }[] {
    const availability = this.selectedInstructorDetails.availability;

    return availability.map((item) => {
      const weekdayNumber = isNaN(+item.weekday) ? DateTimeUtility.convertWeekdayNameToNumber(item.weekday) : +item.weekday;

      return {
        id: item.id,
        name: this.days.find((day) => day.number === weekdayNumber)?.name ?? '',
        time: `${item.timeFrom} - ${item.timeTo} ${this._translateService.instant('Clock')}`,
      };
    });
  }

  get instructorBookings(): { id: number; day: string; date: string; time: string; courseName: string }[] {
    const bookings = this.selectedInstructorDetails.instructorBookings;

    // first filter out past todays date booking
    const filteredBookings = bookings.filter((booking) => new Date(booking.dateFrom).getTime() >= new Date().getTime());

    return filteredBookings.map((item) => {
      return {
        id: item.id,
        day: this.days.find((day) => day.number === new Date(item.dateFrom).getDay())?.name,
        date: item.dateFrom,
        time: `${item.startTime} - ${item.endTime} ${this._translateService.instant('Clock')}`,
        courseName: item.course?.name,
      };
    });
  }

  get roomAvailability(): { id: number; name: string; time: string }[] {
    return this.selectedRoomDetails.availability.map((item) => {
      const weekdayNumber = isNaN(+item.weekday) ? DateTimeUtility.convertWeekdayNameToNumber(item.weekday) : +item.weekday;

      const dayName = this.days.find((day) => day.number === weekdayNumber)?.name ?? '';

      return {
        id: item.id,
        name: dayName,
        time: `${item.timeFrom} - ${item.timeTo} ${this._translateService.instant('Clock')}`,
      };
    });
  }

  get roomBookings(): { id: number; day: string; date: string; time: string; courseName: string }[] {
    const roomBookings = this.selectedRoomDetails.roomBookings;

    // first filter out old bookings
    const filteredBookings = roomBookings.filter((booking) => new Date(booking.dateFrom).getTime() >= new Date().getTime());

    return filteredBookings.map((item) => {
      return {
        id: item.id,
        day: this.days.find((day) => day.number === new Date(item.dateFrom).getDay())?.name,
        date: item.dateFrom,
        time: `${item.startTime} - ${item.endTime} ${this._translateService.instant('Clock')}`,
        courseName: item?.course?.name,
      };
    });
  }

  async downloadDocument(course: CourseEntity) {
    if (course.document) {
      this.loadings.downloadingDocument = true;
      try {
        const filePath = course.document;
        const fileName = filePath.split('/').pop();

        const response = await this._useCourse.downloadDocument(course.id, filePath).toPromise();
        const blob = new Blob([response], { type: 'application/octet-stream' }); // Continue using a generic MIME type

        // Use FileSaver to save the file
        saveAs(blob, fileName || 'course-document');
        this.loadings.downloadingDocument = false;
      } catch (error) {
        log.error('Error downloading the document:', error);
        this.loadings.downloadingDocument = false;
      }
    } else {
      log.error('Course document URL or title is missing.');
    }
  }

  addWeekdaysRow(dayData?: RecurringDate) {
    const courseDate = this.courseDateFormArray;
    const newGroup = this._formBuilder.group(
      {
        dateFrom: [dayData?.dateFrom, [Validators.required]],
        startTime: [dayData?.startTime || null, [Validators.required]],
        endTime: [dayData?.endTime || null, [Validators.required]],
      },
      { validators: timeRangeValidator('startTime', 'endTime') },
    );

    courseDate.push(newGroup);

    courseDate.controls.forEach((group) => {
      const dayControl = group.get('dateFrom');
      if (dayControl) {
        dayControl.setValidators([Validators.required, uniqueFieldValidator(courseDate, 'dateFrom')]);
        dayControl.updateValueAndValidity({ emitEvent: false });
        dayControl.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  removeWeekdaysRow(index: number) {
    const courseDate = this.form.get('courseDate') as FormArray;
    courseDate.removeAt(index);
  }

  selectAllParticipants(taskSelector = false) {
    const controlName = taskSelector ? 'task.userSelect' : 'participants';
    this.form.get(controlName).setValue(this.users.map((user) => user.id));
  }

  unSelectAll(taskSelector = false) {
    const controlName = taskSelector ? 'task.userSelect' : 'participants';
    this.form.get(controlName).setValue([]);
  }

  close() {
    if (!isMobileView()) {
      this._dialogRef.close();
    } else {
      this._bottomSheetService.close();
    }
  }

  save(btnRef?: ButtonComponent) {
    if (this.form.invalid) {
      this.markAllInvalidControlsAndScrollToFirst(this.form, this._elRef);
      this._toast.error(this._translateService.instant('Please fill all required fields'));
      Object.keys(this.form.controls).forEach((field) => {
        const control = this.form.get(field);
        if (control.invalid) {
          console.log(field, control.errors);
        }
      });
      return;
    }

    const formValues = this.form.value as CourseEntity;
    formValues.approvedStatus = 0;
    formValues.dateFrom = formValues.courseDate[0].dateFrom + ' ' + formValues.courseDate[0].startTime;
    formValues.startTime = formValues.courseDate[0].startTime;
    formValues.endTime = formValues.courseDate[formValues.courseDate.length - 1].endTime;
    // append :00 to the end of the startTime and endTime for course recurrence
    formValues.courseRecurrence = formValues.courseDate.map((date) => ({
      ...date,
      startTime: date.startTime + ':00',
      endTime: date.endTime + ':00',
    }));

    /**
     * Recurrence Based Data
     */
    if (!this.form.get('repeatCourse')?.value || this.form.get('repeatCourse')?.value === RecurrenceType.DoesNotRepeat) {
      formValues.dateTo = formValues.courseDate[formValues.courseDate.length - 1].dateFrom + ' ' + formValues.courseDate[formValues.courseDate.length - 1].endTime;
      formValues.dateRepeat = formValues.dateTo;
    } else {
      formValues.dateTo = this.form.get('repeatEndDate')?.value + ' ' + formValues.courseDate[formValues.courseDate.length - 1].endTime;
      formValues.dateRepeat = this.form.get('repeatEndDate')?.value + ' ' + formValues.courseDate[formValues.courseDate.length - 1].endTime;
      if (this.form.get('repeatCourse')?.value !== RecurrenceType.Custom) {
        const repeatOption = this.form.get('repeatCourse')?.value;
        const startDate = new Date(
          Date.UTC(new Date(formValues.courseDate[0].dateFrom).getFullYear(), new Date(formValues.courseDate[0].dateFrom).getMonth(), new Date(formValues.courseDate[0].dateFrom).getDate()),
        );
        // Setting UNTIL to UTC start of the specified end date
        const endDate = new Date(
          Date.UTC(
            new Date(this.form.get('repeatEndDate')?.value).getFullYear(),
            new Date(this.form.get('repeatEndDate')?.value).getMonth(),
            new Date(this.form.get('repeatEndDate')?.value).getDate(),
          ),
        );

        let freq, byday;
        switch (repeatOption) {
          case RecurrenceOption.RepeatDaily:
            freq = RRule.DAILY;
            break;
          case RecurrenceOption.RepeatWeekly:
            freq = RRule.WEEKLY;
            byday = RRule.MO; // Adjust based on actual day needed
            break;
          case RecurrenceOption.RepeatMonthly:
            freq = RRule.MONTHLY;
            break;
          case RecurrenceOption.RepeatYearly:
            freq = RRule.YEARLY;
            break;
          default:
            freq = RRule.WEEKLY;
            break;
        }

        const rruleOptions = {
          freq,
          dtstart: startDate,
          until: endDate,
          byweekday: byday ? [byday] : undefined,
          wkst: RRule.SU,
        };

        const rrule = new RRule(rruleOptions);
        let rruleString = rrule.toString();
        rruleString = `RRULE:${rruleString.match(/FREQ=[^;]+;[^;]+/)[0]};DTSTART=${startDate.toISOString().replace(/[-:]/g, '').replace('.000Z', 'Z')}`;

        formValues.recurrence = rruleString;
      } else {
        formValues.recurrence = formValues.recurrence + ';UNTIL=' + DateTimeUtility.formatDateToUTCString(this.form.get('repeatEndDate')?.value);
      }
    }

    /**
     * Instructor type based recurrence
     */
    if (formValues.instructorType === CourseInstructorType.External) {
      formValues.instructorRecurrence = formValues.courseDate.map((date) => ({
        ...date,
        startTime: date.startTime + ':00',
        endTime: date.endTime + ':00',
      }));
      delete formValues.internalInstructorRecurrence;
    } else if (formValues.instructorType === CourseInstructorType.Internal) {
      formValues.internalInstructorRecurrence = formValues.courseDate.map((date) => ({
        ...date,
        startTime: date.startTime + ':00',
        endTime: date.endTime + ':00',
      }));
      delete formValues.instructorRecurrence;
    }

    /**
     * Participants related data
     */
    if (+formValues.visibility === Visibility.Group && formValues.groupId) {
      formValues.courseUsersArray = this._groupParticipants.map((participant) => ({
        userId: +participant.userId,
        approvedStatus: +participant.userId === +GetUserId() ? 1 : 0,
      }));
    } else if (+formValues.visibility === Visibility.Club) {
      formValues.courseUsersArray = this.users.map((user) => ({
        userId: +user.id,
        approvedStatus: +user.id === +GetUserId() ? 1 : 0,
      }));
    } else {
      if (formValues.participants && formValues.participants.length) {
        formValues.courseUsersArray = this.users
          .filter((user) => (formValues.participants as any).includes(+user.id))
          .map((user) => ({
            userId: +user.id,
            approvedStatus: user.id === +GetUserId() ? 1 : 0,
          }));
      }
    }

    // This below code is commented to remove the authors from being participants as by default, they need to join course in order to become participant

    // const currentUser = formValues.courseUsersArray?.find((user) => user.userId === +GetUserId());
    // if (!currentUser) {
    //   if (!formValues.courseUsersArray || !formValues.courseUsersArray) formValues.courseUsersArray = [];
    //   formValues.courseUsersArray.push({
    //     userId: +GetUserId(),
    //     approvedStatus: 1,
    //   });
    // }

    formValues.chargeable = formValues.pricePerParticipant;
    formValues.createdBy = +GetUserId();
    formValues.teamId = +GetTeamId();

    /**
     * Task related data
     */
    if (formValues.isTask) {
      if (formValues.task.type === TaskType.Group) {
        formValues.task.taskCollaborators = this._taskGroupParticipants.map(
          (user) =>
            ({
              userId: user.userId,
              approvedStatus: 1,
            }) as unknown as TaskCollaborator,
        );

        formValues.task.userSelect = this._taskGroupParticipants
          .filter((user) => user.userId !== +GetUserId())
          .map(
            (user) =>
              ({
                id: user.userId,
                username: user.groupUser[0].fullName,
              }) as CourseTaskUser,
          );

        formValues.task.groups = [
          {
            id: formValues.groupId,
            name: this.groups.find((group) => group.id === formValues.groupId)?.name,
          },
        ];
      } else {
        const taskUsers = (formValues.task as unknown as CourseTask).userSelect;
        formValues.task.taskCollaborators = taskUsers.map(
          (user) =>
            ({
              userId: user,
              approvedStatus: 1,
            }) as unknown as TaskCollaborator,
        );
        formValues.task.groups = '';
        formValues.task.groupId = 0;

        formValues.task.userSelect = this.users
          .filter((user) => (formValues.task.userSelect as any).includes(+user.id))
          .map(
            (user) =>
              ({
                id: +user.id,
                username: user.fullName,
              }) as CourseTaskUser,
          );
      }

      const currentUser = formValues.task.taskCollaborators.find((user) => user.userId === +GetUserId());
      if (!currentUser) {
        formValues.task.taskCollaborators.push({
          userId: +GetUserId(),
          approvedStatus: 1,
        });
      }
    } else {
      formValues.task = null;
    }

    // This below check is omitted as it is not working as expected and availability is mostly handled in backend
    // Final Check For Dates Availability
    // if (!this._checkAvailability(formValues)) return;

    delete formValues.participants;

    const body = removeNullAndUndefinedProperties(instanceToPlain(new CourseEntity(formValues)));

    if (formValues.isTask) {
      body.task = [body.task];
    } else {
      body.task = [];
    }

    if (formValues.instructorType === CourseInstructorType.Internal) {
      body.instructor_internal = [
        {
          user_id: formValues['internalInstructorId'],
        },
      ];
      delete body.instructorReccurance;
      delete body.instructor_external;
    } else if (formValues.instructorType === CourseInstructorType.External) {
      body.instructor_external = [
        {
          instructor_id: formValues['externalInstructorId'],
        },
      ];
      delete body.in_instructorReccurance;
      delete body.instructor_internal;
    }

    // check if

    delete body.image;

    // EXTRA WORK DURING BUG FIXING
    body.tags = [...body.course_tags];

    const formData = this.jsonToFromData(body, [
      'courseDate',
      'courseReccurance',
      'course_tags',
      'tags',
      'course_users',
      'in_instructorReccurance',
      'instructorReccurance',
      'instructor_internal',
      'instructor_external',
      'task',
    ]);

    // TODO: this is the issue from backend as it is breaking when passing course_tags
    formData.delete('course_tags');

    if (this.selectedImage) {
      formData.append('file', this.selectedImage);
    }

    if (this.selectedAttachment) {
      formData.append('file', this.selectedAttachment);
    }

    if (body.course_users && body.course_users.length) {
      body.course_users.forEach((user: any, index: number) => {
        if (+user.user_id !== +GetUserId()) formData.append(`participant[${index}]`, user.user_id);
      });
    }

    const service$ = this._currentSelectedCourse ? this._useCourse.updateCourse(this._currentSelectedCourse.id, formData) : this._useCourse.createCourse(formData);

    this.call(service$, btnRef)
      .then((response) => {
        if (response) {
          if (!this._permissionService.isAdmin()) {
            this._toast.success(this._translateService.instant('Your current Request is waiting for the Approval by Admin'), { duration: 6000 });
          } else {
            const message = this._currentSelectedCourse && this._currentSelectedCourse?.id ? 'Update Successful' : 'Created Successfully';
            this._toast.success(this._translateService.instant(message));
          }

          if (!isMobileView()) {
            this._dialogService.closeAll();
          } else {
            this._bottomSheetService.closeAll();
          }
          this._reloadCurrentRoute();
          this._socketService.emit(SocketEvents.SendNotification, NotificationType.Course);
        } else {
          this._toast.error(this._translateService.instant('Something went wrong'));
        }
      })
      .catch((err) => {
        this._toast.error(this.error, { duration: 5000 });
        log.error('Error while creating or updating course');
      });
  }

  deleteFile(course: CourseEntity) {
    if (!course || !course.id) {
      return;
    }

    this.loadings.deletingDocument = true;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translateService.instant('Image'),
        },
      });
      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this._deleteAttachedFile(course?.id);
              }
              break;
            default:
              this.loadings.deletingDocument = false;
              break;
          }
        },
      });
    } else {
      this._dialogService
        .open(ConfirmationDialogComponent, {
          size: 'md',
          closeButton: false,
          windowClass: 'dialog-overflow-unset',
          data: {
            type: ConfirmationDialogType.DELETE_DOCUMENT,
          },
        })
        .afterClosed$.pipe(untilDestroyed(this))
        .subscribe({
          next: (confirmed: boolean) => {
            if (confirmed) {
              this._deleteAttachedFile(course?.id);
            } else {
              this.loadings.deletingDocument = false;
            }
          },
        });
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------
  protected _initDataForForm() {
    this._initCoursesForm();
    this._handleDatePreselection();
    this._fetchTags();
    this._fetchCourseCommonData();
  }

  protected _fetchCourseDetails(courseId: number) {
    this._useCourse
      .getCourseById(courseId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: CourseEntity) => {
          if (res) {
            this._currentSelectedCourse = res;
            this._patchFormValues(res);
          }
        },
      });
  }

  protected _patchFormValues(course: CourseEntity) {
    const participants = course.courseUsers?.map((participant) => participant.userId).filter((id) => id !== +GetUserId());
    const tags = course.courseTags?.map((tag: any) => tag.tagName);

    this.form.patchValue({
      name: course.name,
      place: course.place,
      type: course.type,
      dateFrom: course.dateFrom,
      dateTo: course.dateTo,
      dateRepeat: course.dateRepeat,
      startTime: course.startTime,
      endTime: course.endTime,
      room: course.room,
      allowedPersons: course.allowedPersons,
      visibility: course.visibility,
      recurrence: course.recurrence,
      showGuestList: course.showGuestList,
      chargeable: course.chargeable && +course.chargeable > 0,
      description: course.description,
      participants: participants,
      courseUsersArray: participants,
      approvedStatus: course.approvedStatus,
      instructorType: course.instructorType,
      internalInstructorId:
        course.courseInternalInstructor && course.courseInternalInstructor.length
          ? course.courseInternalInstructor[0]?.instructorId
          : course.courseExternalInstructor
            ? course.courseExternalInstructor[0]?.instructorId
            : null,
      externalInstructorId: course.courseExternalInstructor ? course.courseExternalInstructor[0]?.instructorId : null,
      pricePerParticipant: course.chargeable ? course.chargeable : 0,
      isTask: !!(course.isTask || course.courseTask.id),
      groupId: course.groupId,
      courseTagsArray: tags,
      image: course.imageUrl ? course.imageUrl : null,
      repeatEndDate: course.dateRepeat,
      repeatCourse: course.recurrence,
    });

    if (course.recurringDates) {
      this.courseDateFormArray.clear();
      this.courseDateFormArray.reset();
      course.recurringDates.forEach((day) => {
        this.addWeekdaysRow(day as RecurringDate);
      });
    }

    if (course.recurrence) {
      try {
        const rrule = RRule.fromString(course.recurrence);

        if (course.recurrence.includes('INTERVAL')) {
          // If 'INTERVAL' is available in string then set to 'Custom'
          this.form.get('repeatCourse').setValue(RecurrenceType.Custom, { emitEvent: false });
          this.showEndDate = true;
          this.form.get('repeatEndDate')?.enable();
          this.form.get('repeatEndDate')?.setValidators([Validators.required, this._repeatEndDateValidator(this.form)]);
          this.form.get('repeatEndDate')?.updateValueAndValidity();
        } else {
          // Otherwise, map 'FREQ' to respective enum values
          switch (rrule.options.freq) {
            case RRule.DAILY:
              this.form.get('repeatCourse').setValue(RecurrenceType.EveryDay);
              break;
            case RRule.WEEKLY:
              this.form.get('repeatCourse').setValue(RecurrenceType.EveryWeek);
              break;
            case RRule.MONTHLY:
              this.form.get('repeatCourse').setValue(RecurrenceType.EveryMonth);
              break;
            case RRule.YEARLY:
              this.form.get('repeatCourse').setValue(RecurrenceType.EveryYear);
              break;
            default:
              this.form.get('repeatCourse').setValue(RecurrenceType.DoesNotRepeat);
          }
        }

        // Set 'repeatEndDate' if applicable
        if (rrule.options.until) {
          const endDate = new Date(rrule.options.until);
          this.form.get('repeatEndDate').setValue(endDate.toISOString().split('T')[0]); // Format YYYY-MM-DD
        }
      } catch {
        this.form.get('repeatCourse').setValue(RecurrenceType.DoesNotRepeat);
      }
    } else {
      // If no recurrence, set to 'Does Not Repeat'
      this.form.get('repeatCourse').setValue(RecurrenceType.DoesNotRepeat);
    }

    if (course.courseTask && course.courseTask.id) {
      const task = course.courseTask;

      const taskType = task.groupId ? TaskType.Group : TaskType.Individual;
      // Filter out the current user's ID from collaborators
      // const taskCollaborators = task.collaborators?.map((collaborator) => collaborator.userId).filter((id) => id !== +GetUserId()) || [];

      this.form.patchValue({
        task: {
          title: task.title,
          description: task.description,
          type: taskType,
          groupId: +task.groupId,
          // userSelect: taskCollaborators,
          status: task.status,
          organizerId: task.organizerId,
          // format date in only date format "YYYY-MM-DD"
          date: task.date.split('T')[0],
        },
      });

      this._fetchTaskCollaborators(course.courseTask.id);
    }

    this.form.markAsPristine();
  }

  private _initCoursesForm() {
    this.form = this._formBuilder.group({
      name: [null, [Validators.required]],
      place: [null, [Validators.required]],
      type: ['course', [Validators.required]],
      dateFrom: [null], // handled in save method
      dateTo: [null], // handled in save method
      dateRepeat: [null], // handled in save method
      startTime: [null], // handled in save method
      endTime: [null], // handled in save method
      room: [null, [Validators.required]],
      allowedPersons: [null, [Validators.required]],
      visibility: [null, [Validators.required]],
      recurrence: [null],
      showGuestList: [false],
      chargeable: [false],
      description: [null, [Validators.required]],
      participants: [[]],
      courseUsersArray: [null], // handled in save method
      author: [GetUserId()],
      approvedStatus: [0],
      instructorType: [null, [Validators.required]],
      internalInstructorId: [null],
      externalInstructorId: [null],
      pricePerParticipant: [{ value: 0, disabled: true }],
      isTask: [false],
      courseDate: this._formBuilder.array([]),
      task: this._formBuilder.group({
        title: [null],
        description: [null],
        room: [null],
        type: [null],
        date: [null],
        organizerId: [`${GetUserId()}`],
        taskCollaborators: [[]],
        groups: [[]],
        groupId: [null],
        status: ['1'],
        userSelect: [null],
      }),

      groupId: [null],
      courseTagsArray: [null],
      image: [null],

      repeatEndDate: [null],
      repeatCourse: [RecurrenceType.DoesNotRepeat],
    });
    this._bindFormValueChanges();
  }

  /**
   * Handles the preselection of date for the event creation.
   * This method checks if a date is provided in the dialog data and preselects it which is passed by Calendar empty cell click.
   * @private
   */
  private _handleDatePreselection() {
    const preselectDate = !isMobileView() ? this._dialogRef?.data?.preselectDate : null;

    if (preselectDate) {
      const preSelectDateForEventCreation: RecurringDate = {
        dateFrom: DateTimeUtility.formatDateToString(preselectDate, 'YYYY-MM-DD'),
        startTime: null,
        endTime: null,
      };
      this.addWeekdaysRow(preSelectDateForEventCreation);
    } else {
      this.addWeekdaysRow();
    }
  }

  private _fetchTags() {
    this._useTags
      .getAllTagsByType(TagType.COURSES)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res && res.length) {
            this.tags = res.map((tag) => ({ label: tag.tagName, value: tag.tagName }));
          }
        },
      });
  }

  private _fetchGroupParticipants(groupId: number, isTaskGroup = false) {
    this._useGroup
      .getApprovedGroupUsers(groupId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res) {
            if (isTaskGroup) {
              this._taskGroupParticipants = res.participants;
              log.info('Task Group Participant set');
            } else {
              this._groupParticipants = res.participants;
              log.info('Group Participant set');
            }
          }
        },
      });
  }

  private _fetchCourseCommonData() {
    this._useCourse
      .getCoursesCommonInfo()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res) {
            this.users = res.users;
            this.groups = res.groups;
            this.rooms = res.rooms;

            this.externalInstructors = res.instructors.filter((instructor: InstructorEntity) => instructor.type === InstructorType.External);
            this.internalInstructors = res.instructors.filter((instructor: InstructorEntity) => instructor.type === InstructorType.Internal);
          }
        },
      });
  }

  private _fetchInstructorDetails(instructorId: number) {
    this._useInstructor
      .getInstructorById(instructorId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res) {
            this.selectedInstructorDetails = res;
          }
        },
      });
  }

  private _fetchRoomDetails(roomId: number) {
    this._useRoom
      .getRoomById(roomId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res) {
            this.selectedRoomDetails = res;
            this.form.get('allowedPersons')?.setValidators([Validators.required, Validators.max(res.noOfPersons)]);
            this.form.get('allowedPersons')?.updateValueAndValidity();
          }
        },
      });
  }

  private _fetchTaskCollaborators(taskId: number) {
    this._useTask
      .getTaskCollaboratorsWithIdsOnly(taskId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.form.get('task.userSelect').setValue(res);
          this.form.get('task.userSelect').markAsPristine();
        },
      });
  }

  private _openCustomRecurrenceForm() {
    if (this.courseDateFormArray.invalid) {
      this._toast.error(this._translateService.instant('Please enter Course date'));

      this.courseDateFormArray.markAllAsTouched();
      this.form.get('repeatCourse')?.setValue(null, { emitEvent: false });
      return;
    }

    const recurringForm = isMobileView()
      ? this._bottomSheetService.open(RecurrenceDatesFormComponent, {
          height: '60%',
          showCloseButton: false,
          data: {
            dateStart: this.courseDateFormArray.at(0).get('dateFrom')?.value,
            currentRecurrence: this._currentSelectedCourse?.recurrence?.includes('INTERVAL') ? this._currentSelectedCourse?.recurrence : null,
          },
        })
      : this._dialogService.open(RecurrenceDatesFormComponent, {
          width: '500px',
          windowClass: 'dialog-overflow-unset',
          data: {
            dateStart: this.courseDateFormArray.at(0).get('dateFrom')?.value,
            currentRecurrence: this._currentSelectedCourse?.recurrence?.includes('INTERVAL') ? this._currentSelectedCourse?.recurrence : null,
          },
        });

    recurringForm.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          this.form.get('recurrence')?.setValue(result);
        } else {
          if (!this._currentSelectedCourse || !this._currentSelectedCourse.recurrence.includes('INTERVAL')) {
            this.form.get('repeatCourse')?.setValue(null, { emitEvent: false });
          }
        }
      },
    });
  }

  private _bindFormValueChanges() {
    // Instructor type based changes
    this.form
      .get('instructorType')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          this.selectedInstructorDetails = null;

          if (value === CourseInstructorType.Internal) {
            this.form.get('internalInstructorId')?.enable({ emitEvent: false });
            this.form.get('externalInstructorId')?.disable({ emitEvent: false });
            this.form.get('externalInstructorId')?.setValue(null, { emitEvent: false });
            this.form.get('internalInstructorId')?.setValidators([Validators.required]);
          } else if (value === CourseInstructorType.External) {
            this.form.get('externalInstructorId')?.enable({ emitEvent: false });
            this.form.get('internalInstructorId')?.disable({ emitEvent: false });
            this.form.get('internalInstructorId')?.setValue(null, { emitEvent: false });
            this.form.get('externalInstructorId')?.setValidators([Validators.required]);
          } else {
            this.form.get('internalInstructorId')?.disable();
            this.form.get('externalInstructorId')?.disable();
            this.form.get('internalInstructorId')?.setValue(null);
            this.form.get('externalInstructorId')?.setValue(null);
            this.form.get('internalInstructorId')?.setValidators(null);
            this.form.get('externalInstructorId')?.setValidators(null);
          }
        },
      });

    // Internal and External Instructor based changes
    this.form
      .get('internalInstructorId')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            this._fetchInstructorDetails(value);
          }
        },
      });

    this.form
      .get('externalInstructorId')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            this._fetchInstructorDetails(value);
          }
        },
      });

    // Visibility Of Course based changes
    this.form
      .get('visibility')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          switch (value) {
            case Visibility.Group:
              this.form.get('participants')?.setValue(null);
              this.form.get('participants')?.disable();
              this.form.get('participants')?.setValidators(null);
              this.form.get('participants')?.updateValueAndValidity();

              this.form.get('groups')?.enable();
              this.form.get('groups')?.setValidators([Validators.required]);
              this.form.get('groups')?.updateValueAndValidity();

              break;
            case Visibility.Public:
              this.form.get('groupId')?.setValue(null);
              this.form.get('groups')?.setValue(null);
              this.form.get('groups')?.disable();
              this.form.get('groups')?.setValidators(null);
              this.form.get('groups')?.updateValueAndValidity();

              this.form.get('participants')?.enable();
              this.form.get('participants')?.setValidators(null); // participants are not required
              this.form.get('participants')?.updateValueAndValidity();

              break;

            case Visibility.Private:
              this.form.get('groupId')?.setValue(null);
              this.form.get('groups')?.setValue(null);
              this.form.get('groups')?.disable();
              this.form.get('groups')?.setValidators(null);
              this.form.get('groups')?.updateValueAndValidity();

              this.form.get('participants')?.enable();
              this.form.get('participants')?.setValidators([Validators.required]);
              this.form.get('participants')?.updateValueAndValidity();

              break;

            default:
              this.form.get('participants')?.disable();
              this.form.get('participants')?.setValidators(null);
              this.form.get('participants')?.updateValueAndValidity();

              this.form.get('groupId')?.setValue(null);
              this.form.get('groups')?.setValue(null);
              this.form.get('groups')?.disable();
              this.form.get('groups')?.setValidators(null);
              this.form.get('groups')?.updateValueAndValidity();
              break;
          }
        },
      });

    // Room based changes
    this.form
      .get('room')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            this._fetchRoomDetails(value);
          }
        },
      });

    // Group selection based changes

    this.form
      .get('groupId')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            this._fetchGroupParticipants(value);
          }
        },
      });

    // Chargeable based changes
    this.form
      .get('chargeable')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            this.form.get('pricePerParticipant')?.enable();
            this.form.get('pricePerParticipant')?.setValidators([Validators.required]);
            this.form.get('pricePerParticipant')?.updateValueAndValidity();
          } else {
            this.form.get('pricePerParticipant')?.disable();
            this.form.get('pricePerParticipant')?.setValidators(null);
            this.form.get('pricePerParticipant')?.updateValueAndValidity();
          }
        },
      });

    // Subscribe to changes in courseDate to manage repeatCourse
    this.form
      .get('courseDate')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          const courseDates = value || [];
          if (courseDates.length > 1) {
            // Disable repeatCourse, clear validators, and set value to null
            this.form.get('repeatCourse')?.setValue(null);
            this.form.get('repeatCourse')?.disable();
            this.form.get('repeatCourse')?.setValidators(null);
            this.form.get('repeatCourse')?.updateValueAndValidity();
            this.showEndDate = false;
            this.form.get('repeatEndDate')?.setValue(null);
            this.form.get('repeatEndDate')?.disable();
            this.form.get('repeatEndDate')?.setValidators(null);
            this.form.get('repeatEndDate')?.updateValueAndValidity();
          } else {
            // Enable repeatCourse, add required validators, and retain current value
            this.form.get('repeatCourse')?.enable();
            this.form.get('repeatCourse')?.setValidators([Validators.required]);
            this.form.get('repeatCourse')?.updateValueAndValidity();
          }
        },
      });

    // Subscribe to changes in repeatCourse to manage repeatEndDate
    this.form
      .get('repeatCourse')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value && value !== RecurrenceType.DoesNotRepeat) {
            // Show and enable repeatEndDate with validators when a repeating course is selected
            this.showEndDate = true;
            this.form.get('repeatEndDate')?.enable();
            this.form.get('repeatEndDate')?.setValidators([Validators.required, this._repeatEndDateValidator(this.form)]);
            this.form.get('repeatEndDate')?.updateValueAndValidity();

            if (value === RecurrenceType.Custom) {
              this._openCustomRecurrenceForm();
            }
          } else {
            // Hide, disable repeatEndDate, clear validators, and set value to null when no repeating course
            this.showEndDate = false;
            this.form.get('repeatEndDate')?.setValue(null);
            this.form.get('repeatEndDate')?.disable();
            this.form.get('repeatEndDate')?.setValidators(null);
            this.form.get('repeatEndDate')?.updateValueAndValidity();
          }
        },
      });

    // isTask based changes
    this.form
      .get('isTask')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (isTaskEnabled) => {
          const taskGroup = this.form.get('task') as FormGroup;
          const repeatEndDate = this.form.get('repeatEndDate') as FormControl;

          if (isTaskEnabled) {
            Object.keys(taskGroup.controls).forEach((field) => {
              if (field === 'title' || field === 'description' || field === 'type' || field === 'date' || field === 'groupId') {
                const control = taskGroup.get(field);
                control?.enable();

                // Apply general required validator to all fields in the task group
                control?.setValidators(Validators.required);

                // Specific case for the date field to include the custom date range validator
                if (field === 'date') {
                  control?.setValidators([Validators.required, this._taskDateValidator(this.courseDateFormArray.controls, repeatEndDate)]);
                }

                control?.updateValueAndValidity();
              }
            });
          } else {
            Object.keys(taskGroup.controls).forEach((field) => {
              const control = taskGroup.get(field);
              control?.disable();
              control?.clearValidators();
              control?.updateValueAndValidity();
            });
          }
        },
      });

    // task.type based changes
    this.form
      .get('task.type')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value === TaskType.Group) {
            this.form.get('task.groupId')?.enable();
            this.form.get('task.groupId')?.setValidators([Validators.required]);
            this.form.get('task.groupId')?.updateValueAndValidity();

            this.form.get('task.userSelect')?.setValue(null);
            this.form.get('task.userSelect')?.disable();
            this.form.get('task.userSelect')?.clearValidators();
            this.form.get('task.userSelect')?.updateValueAndValidity();
          } else {
            this.form.get('task.groupId')?.setValue(null);
            this.form.get('task.groupId')?.disable();
            this.form.get('task.groupId')?.clearValidators();
            this.form.get('task.groupId')?.updateValueAndValidity();

            this.form.get('task.userSelect')?.enable();
            this.form.get('task.userSelect')?.setValidators([Validators.required]);
            this.form.get('task.userSelect')?.updateValueAndValidity();
          }
        },
      });

    this.form
      .get('task.groupId')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            this._fetchGroupParticipants(value, true);
          }
        },
      });
  }

  private _taskDateValidator(courseDateArray: AbstractControl[], repeatEndDate: AbstractControl | null): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // If no date is set, no need to validate
      }

      const taskDate = new Date(control.value);
      const firstcourseDate = courseDateArray.length > 0 ? new Date(courseDateArray[0].get('dateFrom').value) : null;
      const lastcourseDate = courseDateArray.length > 0 ? new Date(courseDateArray[courseDateArray.length - 1].get('dateFrom').value) : null;

      let endDate: Date | null = lastcourseDate;
      let startDate: Date | null = firstcourseDate;

      if (repeatEndDate && repeatEndDate.value) {
        endDate = new Date(repeatEndDate.value);
        startDate = firstcourseDate; // Ensuring that the first course date is used as the start date
      }

      // Check if taskDate is within the range
      if (startDate && endDate) {
        if (taskDate >= startDate && taskDate <= endDate) {
          return null; // No errors, validation passed
        }
        return {
          dateRange: `${this._translateService.instant('Please set the date between')} ${startDate.toISOString().split('T')[0]} - ${endDate.toISOString().split('T')[0]}.`,
        };
      } else {
        return { dateRange: `${this._translateService.instant('Valid date range is not set. Please check the dates.')}` };
      }
    };
  }

  private _repeatEndDateValidator(form: FormGroup): ValidatorFn {
    return (control: AbstractControl): Record<string, any> | null => {
      const courseDates = form.get('courseDate') as FormArray;
      if (courseDates.length > 0) {
        const lastcourseDate = courseDates.at(courseDates.length - 1).get('dateFrom').value;
        const repeatEndDate = control.value;

        // Compare dates
        if (lastcourseDate && repeatEndDate && new Date(repeatEndDate) < new Date(lastcourseDate)) {
          return { dateRangeInvalid: true };
        }
      }
      return null;
    };
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Unused Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  private _checkAvailability(formValues: CourseEntity) {
    const courseDates = this.courseDateFormArray.value;
    const instructorUnavailableDates: any = [];
    const roomUnavailableDates: any = [];

    // Helper function to get all recurrence dates
    const getRecurrenceDates = (startDate: string, endDate: string, recurrenceRule: string): Date[] => {
      const rule = rrulestr(recurrenceRule);
      return rule.between(new Date(startDate), new Date(endDate));
    };

    // Function to check availability for a given date and time
    const checkAvailabilityForDateTime = (dateFrom: string, startTime: string, endTime: string, availability: any[], bookings: any[], unavailableDates: any[], entityType: string) => {
      const dayOfWeek = new Date(dateFrom).getDay();

      // Check if the date and time are within the availability range
      const isWithinAvailability = availability.some((available) => {
        const availabilityDay = this.days.find((day) => day.name === available.name)?.number;
        if (availabilityDay === dayOfWeek) {
          const [availableFrom, availableTo] = available.time.split(' - ');
          return availableFrom <= startTime && availableTo >= endTime;
        }
        return false;
      });

      if (!isWithinAvailability) {
        const msg = this._translateService.instant('Not within availability');
        unavailableDates.push(`${dateFrom} ${startTime} - ${endTime} (${msg})`);
      } else {
        // Check for conflicting bookings
        const conflictingBooking = bookings.find((booking: any) => {
          const bookingDateFrom = new Date(booking.date).toISOString().split('T')[0];
          const bookingStartTime = booking.startTime;
          const bookingEndTime = booking.endTime;

          return (
            bookingDateFrom === dateFrom &&
            ((bookingStartTime <= startTime && bookingEndTime > startTime) || (bookingStartTime < endTime && bookingEndTime >= endTime) || (bookingStartTime >= startTime && bookingEndTime <= endTime))
          );
        });

        if (conflictingBooking) {
          unavailableDates.push(`${dateFrom} ${startTime} - ${endTime}`);
        }
      }
    };

    // Check instructor availability and bookings
    if (this.selectedInstructorDetails) {
      courseDates.forEach((date: any) => {
        const dateFrom = new Date(date.dateFrom).toISOString().split('T')[0];
        const startTime = date.startTime + ':00';
        const endTime = date.endTime + ':00';

        // Check initial date
        checkAvailabilityForDateTime(dateFrom, startTime, endTime, this.instructorAvailability, this.instructorBookings, instructorUnavailableDates, 'instructor');

        // Check recurrence dates if applicable
        if (this.form.get('repeatCourse')?.value && this.form.get('repeatCourse')?.value !== RecurrenceType.DoesNotRepeat) {
          const recurrenceRule = formValues.recurrence;
          const startDate = dateFrom + 'T' + startTime;
          const endDate = this.form.get('repeatEndDate')?.value + 'T' + endTime;
          const recurrenceDates = getRecurrenceDates(startDate, endDate, recurrenceRule);

          recurrenceDates.forEach((recurrenceDate) => {
            const recurrenceDateFrom = recurrenceDate.toISOString().split('T')[0];
            checkAvailabilityForDateTime(recurrenceDateFrom, startTime, endTime, this.instructorAvailability, this.instructorBookings, instructorUnavailableDates, 'instructor');
          });
        }
      });
    }

    // Check room availability and bookings
    if (this.selectedRoomDetails) {
      courseDates.forEach((date: any) => {
        const dateFrom = new Date(date.dateFrom).toISOString().split('T')[0];
        const startTime = date.startTime + ':00';
        const endTime = date.endTime + ':00';

        // Check initial date
        checkAvailabilityForDateTime(dateFrom, startTime, endTime, this.roomAvailability, this.roomBookings, roomUnavailableDates, 'room');

        // Check recurrence dates if applicable
        if (this.form.get('repeatCourse')?.value && this.form.get('repeatCourse')?.value !== RecurrenceType.DoesNotRepeat) {
          const recurrenceRule = formValues.recurrence;
          const startDate = dateFrom + 'T' + startTime;
          const endDate = this.form.get('repeatEndDate')?.value + 'T' + endTime;
          const recurrenceDates = getRecurrenceDates(startDate, endDate, recurrenceRule);

          recurrenceDates.forEach((recurrenceDate) => {
            const recurrenceDateFrom = recurrenceDate.toISOString().split('T')[0];
            checkAvailabilityForDateTime(recurrenceDateFrom, startTime, endTime, this.roomAvailability, this.roomBookings, roomUnavailableDates, 'room');
          });
        }
      });
    }

    // Display errors if any
    if (instructorUnavailableDates.length > 0) {
      const msg = this._translateService.instant('Instructor not available for the following dates and times');
      this._toast.error(`${msg}:  &nbsp; &nbsp; <br> ${instructorUnavailableDates.join(', <br> ')}`, {
        duration: 20000,
        style: {
          border: '1px solid var(--orange)',
        },
      });
    }
    if (roomUnavailableDates.length > 0) {
      const msg = this._translateService.instant('Room not available for the following dates and times');
      this._toast.error(`${msg}: &nbsp; &nbsp; <br> ${roomUnavailableDates.join(', <br> ')}`, {
        duration: 20000,
        style: {
          border: '1px solid var(--orange)',
        },
      });
    }

    // Return true if no conflicts, false otherwise
    return instructorUnavailableDates.length === 0 && roomUnavailableDates.length === 0;
  }

  private _deleteAttachedFile(id: number) {
    if (!id) {
      return;
    }

    this._useCourse
      .deleteCourseFile(id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          if (response) {
            this._toast.success(this._translateService.instant('Delete Successful'));
            this._fetchCourseDetails(id);
          }
          this.loadings.deletingDocument = false;
        },
        error: (error) => {
          this._toast.error(error?.error?.message ?? this._translateService.instant('Something went wrong'));
          log.error('Error deleting file:', error);
          this.loadings.deletingDocument = false;
        },
      });
  }
}
